/**
 * B76C · screen 2d — the onboarding-lite chapter intro.
 *
 * Renders INSIDE the existing `<OnboardingShell>` (the route supplies the
 * chrome via `metaOverride`, the StepZeroFork / CoachOnboarding / AgeStep
 * pattern), so this component is the body only: kicker, serif headline, body,
 * the list card, and the forward CTA.
 *
 * ⚠️ THE KICKER IS A PLACEHOLDER. "Chapter 1 of 2" is the CD handoff's own
 * placeholder pending the onboarding-architecture chapter map; the names and
 * the count both defer to it. It is written once, in
 * `ONBOARDING_LITE_CHAPTER_COPY`, so the later redesign changes one constant.
 *
 * The list rows NAME the queued steps rather than deciding them: the route
 * derives the queue (`deriveOnboardingLiteSteps`) and hands the rows down, so
 * a step this account already answered is not advertised as still to come.
 */

import { StepCta } from '../OnboardingShell/StepLayout';
import { useOnboardingV2 } from '../OnboardingShell/onboardingV2';
import { ONBOARDING_LITE_CHAPTER_COPY, type LiteStepId } from '../../lib/onboardingLite';
import styles from './OnboardingLiteChapter.module.css';

/** The chapter card's row label for each queued question step. */
const ROW_LABEL: Partial<Record<LiteStepId, string>> = {
  journey: ONBOARDING_LITE_CHAPTER_COPY.rowJourney,
  pace: ONBOARDING_LITE_CHAPTER_COPY.rowPace,
  // PAR-Q and body metrics are two steps and ONE row, per the handoff.
  parq: ONBOARDING_LITE_CHAPTER_COPY.rowBody,
  body: ONBOARDING_LITE_CHAPTER_COPY.rowBody,
};

export interface OnboardingLiteChapterProps {
  /** The queue the route derived, age step included (it is not listed). */
  steps: readonly LiteStepId[];
  onContinue: () => void;
}

/** Row labels for the queued steps, in order, de-duplicated. */
export function chapterRows(steps: readonly LiteStepId[]): string[] {
  const out: string[] = [];
  for (const step of steps) {
    const label = ROW_LABEL[step];
    if (label && !out.includes(label)) out.push(label);
  }
  return out;
}

export function OnboardingLiteChapter({ steps, onContinue }: OnboardingLiteChapterProps) {
  const rows = chapterRows(steps);
  // Part 7 (frame 1ap): the shell prints the kicker and the headline, so the body keeps its
  // sentence, the list and the button.
  const v2 = useOnboardingV2();

  return (
    <>
      {!v2 && <p className={styles.kicker}>{ONBOARDING_LITE_CHAPTER_COPY.kicker}</p>}
      {!v2 && <h1 className={styles.headline}>{ONBOARDING_LITE_CHAPTER_COPY.headline}</h1>}
      <p className={styles.body}>{ONBOARDING_LITE_CHAPTER_COPY.body}</p>

      {rows.length > 0 && (
        <ul className={styles.card}>
          {rows.map((label) => (
            <li key={label} className={styles.row}>
              {label}
            </li>
          ))}
        </ul>
      )}

      <StepCta onClick={onContinue}>{ONBOARDING_LITE_CHAPTER_COPY.cta}</StepCta>
    </>
  );
}
